<template>
  <div class="panel-container courses-sidebar px-0">
    <h5 class="gold mb-3 pl-2">
      <i class="fas fa-school"></i> Program Course
    </h5>
    <div v-if="!course" class="text-muted small pl-2">
      No course linked to this program.
    </div>
    <div v-else class="course-card p-2 mb-3 border rounded">
      <div class="d-flex align-items-start justify-content-between mb-2">
        <div>
          <b v-text="course.title"></b>
          <TextClamp :html="course.description" text-class="small text-muted mb-0" />
        </div>
        <span class="course-status-badge" :class="courseBadge.badgeClass">
          <i class="fas" :class="courseBadge.icon"></i>
          <span class="status-text">{{ courseBadge.text }}</span>
        </span>
      </div>

      <!-- Course Start Date Notice -->
      <div v-if="!courseStarted" class="course-start-notice p-3 mb-2 text-center">
        <i class="fas fa-calendar-alt fa-2x mb-2"></i>
        <p class="mb-1 font-weight-bold">Course Not Yet Available</p>
        <p class="mb-0 small">This course starts on <strong>{{ startDate }}</strong></p>
      </div>

      <!-- No Content Notice -->
      <div v-if="course.status === 'no-content'" class="course-no-content-notice p-3 mb-2 text-center">
        <i class="fas fa-inbox fa-2x mb-2"></i>
        <p class="mb-0 small">No content has been added to this course yet.</p>
      </div>

      <!-- Progress Bar (only show when course has started and has content) -->
      <div v-if="courseStarted && course.status !== 'no-content'" class="course-progress mb-2">
        <template v-if="course.status !== 'not-started' && course.sections && course.sections.length > 0">
          <div class="progress-info d-flex justify-content-between mb-1">
            <small class="text-muted">Progress</small>
            <small class="font-weight-bold" :style="{ color: courseBadge.progressColor }">
              {{ course.progress_percent }}%
            </small>
          </div>
          <div class="progress" style="height: 6px;">
            <div
              class="progress-bar"
              :class="courseBadge.progressClass"
              :style="{ width: course.progress_percent + '%' }"
            ></div>
          </div>
          <div v-if="course.total_time" class="total-time-info mt-2 text-right">
            <small class="text-muted">
              <i class="fas fa-clock mr-1"></i>
              Total time: <strong>{{ course.total_time }}</strong>
            </small>
          </div>
        </template>
        <div v-if="course.status === 'not-started'" class="start-course-btn text-right">
          <b-button
            variant="outline-gold"
            size="sm"
            @click="startCourse(course)"
          >
            <i class="fas fa-play mr-1"></i> Start Course
          </b-button>
        </div>
      </div>

      <!-- Course Sections -->
      <div v-if="course.sections && course.sections.length > 0 && courseStarted" class="course-sections mt-3">
        <div class="sections-header p-2 d-flex align-items-center justify-content-between">
          <span class="font-weight-bold">
            <i class="fas fa-list-ul mr-1"></i> Course Sections
          </span>
          <span class="sections-count">
            {{ course.completed_sections_count }}/{{ course.sections.length }}
          </span>
        </div>
        <div class="sections-list">
          <div
            v-for="(section, index) in course.sections"
            :key="section.pkid"
            class="section-item"
            :class="{ 'section-locked': section.is_locked }"
          >
            <div
              class="section-header p-3 d-flex align-items-center justify-content-between"
              @click.stop="handleSectionClick(section, index, course)"
            >
              <div class="d-flex align-items-center flex-grow-1 section-title-wrap">
                <span class="section-number mr-2" :class="sectionStatusMap[section.pkid].numberClass">{{ index + 1 }}</span>
                <span v-text="section.title" class="section-title"></span>
              </div>
              <div class="d-flex align-items-center section-badges">
                <span v-if="section.time_taken" class="time-badge mr-2">
                  <i class="fas fa-clock"></i>
                  {{ section.time_taken }}
                </span>
                <span v-if="sectionHasLessons(section)" class="lesson-count-badge mr-2" :class="{ 'lesson-count-badge-disabled': section.is_locked }">
                  <i class="fas fa-list-ul"></i>
                  {{ section.lesson_stats.done }}/{{ section.lesson_stats.total }}
                </span>
                <span class="status-badge mr-3" :class="sectionStatusMap[section.pkid].badgeClass">
                  <i class="fas" :class="sectionStatusMap[section.pkid].icon"></i>
                  <span class="status-text">{{ sectionStatusMap[section.pkid].text }}</span>
                </span>
                <i
                  class="fas section-chevron"
                  :class="isSectionExpanded(course.pkid, section.pkid) ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
              </div>
            </div>
            <b-collapse :visible="isSectionExpanded(course.pkid, section.pkid)">
              <div v-if="isSectionExpanded(course.pkid, section.pkid)" class="section-content">
                <!-- Locked section: show description + unlock notice + disabled lessons preview -->
                <div v-if="!shouldShowContent(section)">
                  <div class="p-3 pb-1">
                    <div v-if="section.sub_title" class="mb-2">
                      <span class="text-muted font-italic">{{ section.sub_title }}</span>
                    </div>
                    <TextClamp :html="section.description" />
                  </div>
                  <div v-if="sectionHasLessons(section)" class="lessons-list lessons-list-locked">
                    <div
                      v-for="(lesson, lIndex) in section.subsections"
                      :key="lesson.id"
                      class="lesson-item"
                    >
                      <div class="lesson-header-locked d-flex align-items-center justify-content-between">
                        <div class="d-flex align-items-center lesson-left">
                          <span class="lesson-number-locked mr-3">{{ lIndex + 1 }}</span>
                          <div>
                            <span class="lesson-title-locked" v-text="lesson.title"></span>
                          </div>
                        </div>
                        <i class="fas fa-lock lesson-lock-icon"></i>
                      </div>
                    </div>
                  </div>

                  <div v-if="section.unlock_date" class="unlock-date-notice m-3 p-2 text-center">
                    <i class="fas fa-calendar-alt mr-1"></i>
                    Available from <strong>{{ section.unlock_date }}</strong>
                  </div>
                </div>

                <!-- Unlocked section: show section content + lessons accordion -->
                <template v-else>
                  <div v-if="section.sub_title || section.description" class="p-3 pb-1">
                    <div v-if="section.sub_title" class="mb-2">
                      <span class="text-muted font-italic">{{ section.sub_title }}</span>
                    </div>
                    <TextClamp v-if="section.description" :html="section.description" />
                  </div>
                  <div v-if="section.subsections && section.subsections.length > 0">
                  </div>
                  <div v-else class="text-muted text-center py-3">
                    <i class="fas fa-book-open mr-2"></i>No lessons have been added to this section yet.
                  </div>

                  <!-- Lessons list -->
                  <div v-if="section.subsections && section.subsections.length > 0" class="lessons-list">
                    <div
                      v-for="(lesson, lIndex) in section.subsections"
                      :key="lesson.id"
                      class="lesson-item"
                    >
                      <div
                        class="lesson-header d-flex align-items-center justify-content-between"
                        :class="{ 'lesson-done': isLessonDone(lesson.id) }"
                        @click="toggleLesson(section.pkid, lesson.id)"
                      >
                        <div class="d-flex align-items-center lesson-left">
                          <span class="lesson-number mr-3" :class="{ 'lesson-number-done': isLessonDone(lesson.id) }">
                            <i v-if="isLessonDone(lesson.id)" class="fas fa-check"></i>
                            <span v-else>{{ lIndex + 1 }}</span>
                          </span>
                          <div>
                            <span class="lesson-title" :class="{ 'lesson-title-done': isLessonDone(lesson.id) }" v-text="lesson.title"></span>
                          </div>
                        </div>
                        <div class="d-flex align-items-center">
                          <span v-if="isLessonDone(lesson.id)" class="lesson-done-tag mr-2">Completed</span>
                          <i
                            class="fas lesson-chevron"
                            :class="isLessonExpanded(section.pkid, lesson.id) ? 'fa-chevron-up' : 'fa-chevron-down'"
                          ></i>
                        </div>
                      </div>
                      <b-collapse :visible="isLessonExpanded(section.pkid, lesson.id)">
                        <div class="lesson-content">
                          <CourseMeditation
                            :key="'lesson_' + lesson.id"
                            :data="getLessonData(lesson, section, course)"
                            :inline-mode="true"
                            @section-opened="onSectionOpened"
                          />
                          <div v-if="!isLessonDone(lesson.id)" class="lesson-action">
                            <b-button
                              size="sm"
                              variant="outline-success"
                              class="mark-done-btn"
                              @click.stop="markLessonDone(lesson)"
                            >
                              <i class="fas fa-check mr-1"></i> Mark as Done
                            </b-button>
                          </div>
                        </div>
                      </b-collapse>
                    </div>
                  </div>

                </template>
              </div>
            </b-collapse>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from "@/axios";
import CourseMeditation from '../CourseMeditation.vue';
import TextClamp from './TextClamp.vue';

export default {
  name: 'ProgramCourse',
  components: { CourseMeditation, TextClamp },
  props: {
    courseId: { type: [String, Number], default: null },
    activeChallengeId: { type: [String, Number], default: null },
    startDate: { type: String, default: null },
    challengeType: { type: String, default: null },
  },
  data() {
    return {
      course: null,
      expandedSections: {},
      expandedLessons: {},
      localLessonStatus: {},
    };
  },
  computed: {
    courseStarted() {
      return !!this.course && this.course.status !== 'not-available';
    },

    courseBadge() {
      const badges = {
        'not-available': { text: 'Not Available', badgeClass: 'course-status-not-available', icon: 'fa-calendar-alt',  progressClass: 'bg-secondary',           progressColor: '#6c757d' },
        'no-content':    { text: 'No Content',    badgeClass: 'course-status-empty',         icon: 'fa-inbox',         progressClass: 'bg-secondary',           progressColor: '#6c757d' },
        'not-started':   { text: 'Not Started',   badgeClass: 'course-status-not-started',   icon: 'fa-circle',        progressClass: 'bg-secondary',           progressColor: '#6c757d' },
        'in-progress':   { text: 'In Progress',   badgeClass: 'course-status-in-progress',   icon: 'fa-clock',         progressClass: 'progress-bar-in-progress', progressColor: '#d6b674' },
        'completed':     { text: 'Completed',     badgeClass: 'course-status-completed',     icon: 'fa-check-circle',  progressClass: 'progress-bar-completed',   progressColor: '#d6b674' },
      };
      return badges[this.course?.status] || badges['not-started'];
    },

    sectionStatusMap() {
      if (!this.course?.sections) return {};
      const map = {};
      this.course.sections.forEach(section => {
        const badges = {
          'completed':   { text: 'Completed',   badgeClass: 'status-completed',   numberClass: 'completed',   icon: 'fa-check'  },
          'in-progress': { text: 'In Progress', badgeClass: 'status-in-progress', numberClass: 'in-progress', icon: 'fa-play'   },
          'not-started': { text: 'Not Started', badgeClass: 'status-not-started', numberClass: '',            icon: 'fa-circle' },
          'locked':      { text: 'Locked',      badgeClass: 'status-not-started', numberClass: 'locked',      icon: 'fa-lock'   },
        };
        let badge = badges[section.status] || badges['not-started'];
        if (section.status === 'locked' && section.unlock_date) {
          badge = { ...badge, text: 'Available ' + section.unlock_date };
        }
        map[section.pkid] = badge;
      });
      return map;
    },

    lessonStatusMap() {
      if (!this.course?.sections) return {};
      const map = {};
      this.course.sections.forEach(section => {
        (section.subsections || []).forEach(lesson => {
          map[lesson.id] = lesson.status;
        });
      });
      return map;
    },
  },
  methods: {
    fetchCourse() {
      axios.get(`programs/${this.activeChallengeId}/course-with-progress`)
        .then(response => {
          if (response.data.course) {
            this.course = response.data.course;
            this.localLessonStatus = {};
          }
        })
        .catch(() => console.log('Error: fetchCourse.'));
    },

    handleSectionClick(section, index, course) {
      if (!this.courseStarted) return;
      const key = `${course.pkid}_${section.pkid}`;
      const isExpanding = !this.expandedSections[key];
      this.toggleSection(course.pkid, section.pkid);
      if (isExpanding && section.status === 'not-started' && !section.is_locked) {
        axios.post(`programs/sections/start/${this.activeChallengeId}/${section.pkid}`)
          .then(() => this.fetchCourse())
          .catch(() => console.log('Error: handleSectionClick start.'));
      }
    },

    shouldShowContent(section) {
      return this.courseStarted && !section.is_locked;
    },

    startCourse(course) {
      if (!course.sections?.length) return;
      const firstSection = course.sections[0];
      const key = `${course.pkid}_${firstSection.pkid}`;
      this.$set(this.expandedSections, key, true);
      this.saveExpandedSections();
      axios.post(`programs/sections/start/${this.activeChallengeId}/${firstSection.pkid}`)
        .then(() => this.fetchCourse())
        .catch(() => console.log('Error: startCourse.'));
    },

    onSectionOpened() {
      this.fetchCourse();
    },

    toggleSection(courseId, sectionId) {
      const key = `${courseId}_${sectionId}`;
      this.$set(this.expandedSections, key, !this.expandedSections[key]);
      this.saveExpandedSections();
    },

    isSectionExpanded(courseId, sectionId) {
      return !!this.expandedSections[`${courseId}_${sectionId}`];
    },

    saveExpandedSections() {
      const challengeId = this.$route.params.challenge_id;
      localStorage.setItem(`expandedSections_${challengeId}`, JSON.stringify(this.expandedSections));
    },

    toggleLesson(sectionId, lessonId) {
      const key = `${sectionId}_${lessonId}`;
      this.$set(this.expandedLessons, key, !this.expandedLessons[key]);
    },

    isLessonExpanded(sectionId, lessonId) {
      return !!this.expandedLessons[`${sectionId}_${lessonId}`];
    },

    isLessonDone(lessonId) {
      if (this.localLessonStatus[lessonId] === 'completed') return true;
      return this.lessonStatusMap[lessonId] === 'completed';
    },

    markLessonDone(lesson) {
      this.$set(this.localLessonStatus, lesson.id, 'completed');

      axios.get(`programs/lessons/complete/${lesson.id}/${this.activeChallengeId}`)
        .then(() => this.fetchCourse())
        .catch(() => {
          const reverted = { ...this.localLessonStatus };
          delete reverted[lesson.id];
          this.localLessonStatus = reverted;
        });
    },

    sectionHasLessons(section) {
      return !!(section.lesson_stats && section.lesson_stats.total > 0);
    },

    isMeditationCourse(course) {
      return course.category == 3;
    },

    getSectionData(section, course) {
      return {
        ...section,
        type: this.isMeditationCourse(course) ? 'CourseMeditation' : null,
        challenge_id: this.activeChallengeId,
        course_id: course.id,
        progress: section.user_progress || {},
      };
    },

    getLessonData(lesson, section, course) {
      return {
        ...lesson,
        type: this.isMeditationCourse(course) ? 'CourseMeditation' : null,
        challenge_id: this.activeChallengeId,
        course_id: course.id,
        section_id: section.pkid,
        progress: section.user_progress || {},
      };
    },
  },
  mounted() {
    if (this.courseId) {
      this.fetchCourse();
    }
  },
  watch: {
    courseId(newVal) {
      if (newVal) this.fetchCourse();
    },
  },
};
</script>

<style scoped>
.panel-container {
  max-height: 765px;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 15px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background-color: #f8f9fa;
  flex: 1;
}

.courses-sidebar {
  height: 100%;
}

.course-card {
  background-color: #fff;
  transition: box-shadow 0.3s ease;
}

.course-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Course Status Badge */
.course-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
}

.course-status-not-started {
  background-color: #f5f5f5;
  color: #888;
  border: 1px solid #e0e0e0;
}

.course-status-in-progress {
  background: linear-gradient(135deg, rgba(214, 182, 116, 0.2) 0%, rgba(196, 165, 90, 0.2) 100%);
  color: #b8963e;
  border: 1px solid rgba(214, 182, 116, 0.5);
}

.course-status-completed {
  background: linear-gradient(135deg, rgba(214, 182, 116, 0.25) 0%, rgba(196, 165, 90, 0.25) 100%);
  color: #b8963e;
  border: 1px solid rgba(214, 182, 116, 0.6);
}

.course-status-empty {
  background-color: #f5f5f5;
  color: #aaa;
  border: 1px solid #e0e0e0;
}

.course-status-not-available {
  background: linear-gradient(135deg, rgba(214, 182, 116, 0.15) 0%, rgba(196, 165, 90, 0.15) 100%);
  color: #b8963e;
  border: 1px solid rgba(214, 182, 116, 0.4);
}

/* Course Progress Bar */
.course-progress {
  margin-top: 10px;
}

.course-progress .progress {
  background-color: #e9ecef;
  border-radius: 10px;
  overflow: hidden;
}

.course-progress .progress-bar {
  transition: width 0.5s ease;
  border-radius: 10px;
}

.progress-bar-in-progress {
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
}

.progress-bar-completed {
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
}

/* Sections Count Badge */
.sections-count {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
  color: #fff;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

@media (max-width: 767px) {
  .courses-sidebar {
    margin-top: 20px;
  }
}

.course-sections {
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #dee2e6;
}

.sections-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.sections-list {
  max-height: 450px;
  overflow-y: auto;
}

.section-item {
  border-bottom: 1px solid #eee;
  background-color: #fff;
}

.section-item:last-child {
  border-bottom: none;
}

.section-header {
  cursor: pointer;
  transition: background-color 0.2s;
}

.section-header:hover {
  background-color: #f8f9fa;
}

.section-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
  color: #fff;
  border-radius: 50%;
  font-size: 0.8rem;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(214, 182, 116, 0.3);
  transition: all 0.3s ease;
}

.section-number.in-progress {
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
  box-shadow: 0 2px 8px rgba(214, 182, 116, 0.4);
}

.section-number.completed {
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
  box-shadow: 0 2px 8px rgba(214, 182, 116, 0.5);
}

/* Time Badge Styles */
.time-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 15px;
  font-size: 0.7rem;
  font-weight: 500;
  background: rgba(214, 182, 116, 0.15);
  color: #b8963e;
  border: 1px solid rgba(214, 182, 116, 0.3);
}

.time-badge i {
  font-size: 0.65rem;
  color: #d6b674;
}

/* Status Badge Styles */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.status-badge i {
  font-size: 0.7rem;
}

/* Not Started */
.status-not-started {
  background-color: #f0f0f0;
  color: #888;
  border: 1px solid #e0e0e0;
}

.status-not-started i {
  opacity: 0.5;
}

/* Locked section */
.section-number.locked {
  background: linear-gradient(135deg, #9e9e9e 0%, #757575 100%);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.section-item.section-locked {
  opacity: 0.7;
}

.section-item.section-locked .section-title {
  color: #999;
}

.section-item.section-locked .section-header:hover {
  background-color: #fff;
}

.btn-locked {
  cursor: not-allowed !important;
}

/* Start Course Button */
.btn-outline-gold {
  color: #b8963e;
  border-color: #d6b674;
  background: transparent;
}

.btn-outline-gold:hover {
  color: #fff;
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
  border-color: #d6b674;
}

.btn-outline-gold:focus {
  box-shadow: 0 0 0 0.2rem rgba(214, 182, 116, 0.5);
}

.start-course-btn .btn {
  font-weight: 500;
  padding: 6px 16px;
  font-size: 0.8rem;
  border-radius: 18px;
  transition: all 0.3s ease;
}

/* In Progress */
.status-in-progress {
  background: linear-gradient(135deg, rgba(214, 182, 116, 0.15) 0%, rgba(196, 165, 90, 0.15) 100%);
  color: #b8963e;
  border: 1px solid rgba(214, 182, 116, 0.4);
}

.status-in-progress i {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Completed */
.status-completed {
  background: linear-gradient(135deg, rgba(214, 182, 116, 0.2) 0%, rgba(196, 165, 90, 0.2) 100%);
  color: #b8963e;
  border: 1px solid rgba(214, 182, 116, 0.5);
}

.status-completed i {
  color: #d6b674;
}

/* Hover effects */
.section-header:hover .status-badge {
  transform: translateX(-2px);
}

.section-header:hover .section-number {
  transform: scale(1.05);
}

/* Chevron icon */
.section-chevron {
  color: #aaa;
  font-size: 0.8rem;
  transition: transform 0.3s ease, color 0.3s ease;
}

.section-header:hover .section-chevron {
  color: #d6b674;
}

/* ─── Mobile responsive ─── */
@media (max-width: 576px) {

  /* Panel: remove fixed height so content never gets clipped */
  .panel-container {
    max-height: none;
    padding: 12px;
    border-radius: 0;
    border-left: none;
    border-right: none;
  }

  /* Sections list: let it scroll naturally with the page */
  .sections-list {
    max-height: none;
    overflow-y: visible;
  }

  /* Course status badge — icon-only circle */
  .course-status-badge .status-text { display: none; }
  .course-status-badge {
    padding: 8px;
    border-radius: 50%;
    min-width: 32px;
    min-height: 32px;
    justify-content: center;
  }

  /* Section header — tighter padding, no hover transform */
  .section-header {
    padding: 12px 10px !important;
  }
  .section-header:hover .status-badge { transform: none; }
  .section-header:hover .section-number { transform: none; }

  /* Section number — slightly smaller on mobile */
  .section-number {
    width: 24px;
    height: 24px;
    font-size: 0.72rem;
    flex-shrink: 0;
  }

  /* Section title — slightly smaller */
  .section-title {
    font-size: 0.88rem;
  }

  /* Time badge — hide it, save space for title */
  .time-badge { display: none; }

  /* Status badge — icon-only circle */
  .status-text { display: none; }
  .status-badge {
    padding: 5px 7px;
    border-radius: 50%;
    min-width: 28px;
    min-height: 28px;
    justify-content: center;
    margin-right: 2px !important;
  }

  /* Lesson header — bigger touch target, aligned indent */
  .lesson-header {
    padding: 13px 10px 13px 12px;
    min-height: 52px;
  }

  /* Lesson content — remove deep 50px left indent, full-width feel */
  .lesson-content {
    padding: 16px 12px;
  }

  /* Audio player — stretch to full width inside lesson content */
  ::v-deep .lesson-content audio {
    width: 100%;
    min-height: 42px;
    border-radius: 8px;
  }

  /* Video embeds — already use embed-responsive, ensure no overflow */
  ::v-deep .lesson-content .embed-responsive {
    border-radius: 8px;
    overflow: hidden;
  }

  /* Lesson text — readable, no horizontal overflow */
  ::v-deep .lesson-content pre {
    font-size: 0.88rem;
    line-height: 1.6;
    padding: 0 !important;
    white-space: pre-wrap;
    word-break: break-word;
    overflow-wrap: break-word;
    overflow-x: hidden;
  }

  /* Long URLs must wrap */
  ::v-deep .lesson-content a {
    word-break: break-all;
  }

  /* Mark as Done — full-width button, easy to tap */
  .lesson-action {
    margin-top: 14px;
    justify-content: stretch;
  }
  .mark-done-btn {
    width: 100%;
    justify-content: center;
    padding: 9px 14px;
    font-size: 0.82rem;
    border-radius: 10px;
  }

  /* Lesson done tag — smaller text */
  .lesson-done-tag {
    font-size: 0.6rem;
  }

  /* Lesson count badge — compact */
  .lesson-count-badge {
    padding: 2px 6px;
    font-size: 0.68rem;
  }
}

.section-title-wrap {
  min-width: 0;
}

.section-badges {
  flex-shrink: 0;
  gap: 4px;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 500;
  word-break: break-word;
  overflow-wrap: break-word;
}

.section-content {
  background-color: #f8f9fa;
  border-top: 1px solid #eee;
  font-size: 0.9rem;
}

/* No Content Notice */
.course-no-content-notice {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  color: #999;
}

.course-no-content-notice i {
  color: #bbb;
}

/* Course Start Date Notice */
.course-start-notice {
  background: linear-gradient(135deg, rgba(214, 182, 116, 0.1) 0%, rgba(196, 165, 90, 0.1) 100%);
  border: 1px solid rgba(214, 182, 116, 0.3);
  border-radius: 8px;
  color: #b8963e;
}

.course-start-notice i {
  color: #d6b674;
}

.unlock-date-notice {
  background: linear-gradient(135deg, rgba(214, 182, 116, 0.1) 0%, rgba(196, 165, 90, 0.1) 100%);
  border: 1px solid rgba(214, 182, 116, 0.3);
  border-radius: 6px;
  color: #b8963e;
  font-size: 0.85rem;
}

.unlock-date-notice i {
  color: #d6b674;
}

/* Locked lessons preview */
.lessons-list-locked {
  border-top: 2px solid #e8e8e8;
  background-color: #f2f4f6;
  padding: 6px 0 6px 12px;
  opacity: 0.65;
  pointer-events: none;
}

.lesson-header-locked {
  padding: 9px 12px;
  background-color: #fff;
  cursor: not-allowed;
  border-radius: 6px;
  margin: 4px 8px 4px 0;
  border-left: 3px solid #ddd;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}

.lesson-number-locked {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #ebebeb;
  color: #888;
  border: 1px solid #d5d5d5;
  border-radius: 50%;
  font-size: 0.7rem;
  font-weight: 600;
  flex-shrink: 0;
}

.lesson-title-locked {
  font-size: 0.87rem;
  font-weight: 500;
  color: #888;
}

.lesson-lock-icon {
  color: #bbb;
  font-size: 0.7rem;
  margin-right: 14px;
}

/* Lessons accordion */
.lessons-list {
  padding: 4px 0 12px 4px;
}

.lesson-item {
  border-bottom: 1px solid #e6e8ea;
  background-color: #fff;
  border-radius: 6px;
  margin: 4px 8px 4px 0;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  border-left: 3px solid #d6b674;
}

.lesson-item:last-child {
  border-bottom: none;
}

.lesson-header {
  cursor: pointer;
  padding: 9px 12px 9px 12px;
  background-color: #fff;
  transition: background-color 0.2s;
}

.lesson-header:hover {
  background-color: #fdf9f2;
}

.lesson-left {
  min-width: 0;
  flex: 1;
}

.lesson-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #f5f5f5;
  color: #999;
  border: 1px solid #e0e0e0;
  border-radius: 50%;
  font-size: 0.7rem;
  font-weight: 600;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.lesson-number-done {
  background: rgba(214, 182, 116, 0.15);
  border-color: rgba(214, 182, 116, 0.5);
  color: #b8963e;
}

.lesson-number-done i {
  font-size: 0.65rem;
}

.lesson-title {
  font-size: 0.87rem;
  font-weight: 500;
  color: #555;
}

.lesson-title-done {
  color: #888;
}

.lesson-done-tag {
  font-size: 0.68rem;
  font-weight: 500;
  color: #b8963e;
  opacity: 0.8;
  white-space: nowrap;
  margin-right: 4px;
}

.lesson-chevron {
  color: #ccc;
  font-size: 0.7rem;
  transition: color 0.2s;
  flex-shrink: 0;
  margin-left: 6px;
}

.lesson-header:hover .lesson-chevron {
  color: #d6b674;
}

.lesson-content {
  background-color: #f8f9fa;
  border-top: 1px solid #eee;
  padding: 14px;
  font-size: 0.9rem;
  overflow-x: hidden;
  word-break: break-word;
  overflow-wrap: break-word;
}

.lesson-action {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
}

.mark-done-btn {
  border-radius: 16px;
  font-size: 0.78rem;
  font-weight: 500;
  padding: 4px 14px;
  color: #b8963e;
  border-color: rgba(214, 182, 116, 0.6);
  background: transparent;
  transition: all 0.2s;
}

.mark-done-btn:hover {
  background: rgba(214, 182, 116, 0.12);
  border-color: #d6b674;
  color: #b8963e;
}

.lesson-count-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 0.72rem;
  font-weight: 600;
  background: rgba(214, 182, 116, 0.12);
  color: #b8963e;
  border: 1px solid rgba(214, 182, 116, 0.3);
}

.lesson-count-badge-disabled {
  background: #f0f0f0;
  color: #bbb;
  border-color: #e0e0e0;
}

.lesson-count-badge i {
  font-size: 0.65rem;
}

</style>
